/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InvalidateQueryFilters,
  QueryClient,
  useQuery,
  UseQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  ListExternalKeysQueryParamProvider,
  ListExternalKeysRequest,
  ListExternalKeysSecurity,
} from "../models/operations/listexternalkeys.js";
import { useGramContext } from "./_context.js";
import {
  QueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildListExternalKeysQuery,
  ListExternalKeysQueryData,
  prefetchListExternalKeys,
  queryKeyListExternalKeys,
} from "./listExternalKeys.core.js";
export {
  buildListExternalKeysQuery,
  type ListExternalKeysQueryData,
  prefetchListExternalKeys,
  queryKeyListExternalKeys,
};

export type ListExternalKeysQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * listExternalKeys externalKeys
 *
 * @remarks
 * List the organization's external keys (provider-independent summary). Optionally filter by provider. Requires org:read.
 */
export function useListExternalKeys(
  request?: ListExternalKeysRequest | undefined,
  security?: ListExternalKeysSecurity | undefined,
  options?: QueryHookOptions<
    ListExternalKeysQueryData,
    ListExternalKeysQueryError
  >,
): UseQueryResult<ListExternalKeysQueryData, ListExternalKeysQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildListExternalKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * listExternalKeys externalKeys
 *
 * @remarks
 * List the organization's external keys (provider-independent summary). Optionally filter by provider. Requires org:read.
 */
export function useListExternalKeysSuspense(
  request?: ListExternalKeysRequest | undefined,
  security?: ListExternalKeysSecurity | undefined,
  options?: SuspenseQueryHookOptions<
    ListExternalKeysQueryData,
    ListExternalKeysQueryError
  >,
): UseSuspenseQueryResult<
  ListExternalKeysQueryData,
  ListExternalKeysQueryError
> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildListExternalKeysQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

export function setListExternalKeysData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      provider?: ListExternalKeysQueryParamProvider | undefined;
      gramSession?: string | undefined;
    },
  ],
  data: ListExternalKeysQueryData,
): ListExternalKeysQueryData | undefined {
  const key = queryKeyListExternalKeys(...queryKeyBase);

  return client.setQueryData<ListExternalKeysQueryData>(key, data);
}

export function invalidateListExternalKeys(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      provider?: ListExternalKeysQueryParamProvider | undefined;
      gramSession?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalKeys", "list", ...queryKeyBase],
  });
}

export function invalidateAllListExternalKeys(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "externalKeys", "list"],
  });
}
